import React, { createRef } from 'react';
import {
  initBoard,
  getItems,
  moveItem,
  insertItem,
  insertItemIfEmpty,
  removeItem,
  getOverlaps,
  getEmptyPosition,
} from './gridcolalg';
import Draggable from 'react-draggable';
import { Motion, presets, spring } from '@serprex/react-motion';
import { GridContext } from './GridRoot';

import type {
  TItem,
  TItemAttrs,
  TGridBoard,
  IGridItem,
  TPlaceholder,
} from './types';

export interface GridMainProps {
  col: number;
  row: number;
  width: number;
  height: number;
  items: TItem[];
  delIcon?: React.ReactNode;
  mainBg?: string;
  onChange?: (v: TItem[]) => void;
  onInsert?: (success: boolean, v: TItem) => void;
  onRemove?: (success: boolean, item: TItem) => void;
  onBeforeDrop?: (
    maybeAPp: TItem,
    maybeFolder: TItem,
    itemAttr: TItemAttrs,
  ) => TItemAttrs | undefined;
  onBeforeRemove?: (item: TItem) => boolean;
}

export interface GridMainState {
  board: TGridBoard;
  hideDel: boolean;
  inserting: string;
}

export const ranNum = (number = 10): string => {
  const str = '987654321';
  let tmp = '';
  const len = str.length;
  for (let i = 0; i < number; i++) {
    tmp += str.charAt(Math.floor(Math.random() * len));
  }
  return tmp;
};

class GridMain extends React.Component<GridMainProps, GridMainState> {
  static contextType = GridContext;

  context!: React.ContextType<typeof GridContext>;

  itemAttrs: TItemAttrs;

  unitWidth: number;

  unitHeight: number;

  mainRef = createRef<HTMLDivElement>();

  mainPageX = 0;

  mainPageY = 0;

  constructor(props: any) {
    super(props);
    const { col, row, width, height, items } = this.props;

    const { itemAttrs, gridItems } = this.parseItems(items);

    this.itemAttrs = itemAttrs;
    this.unitHeight = height / row;
    this.unitWidth = width / col;

    this.state = {
      board: initBoard(col, row, gridItems),
      hideDel: false,
      inserting: '',
    };
  }

  componentDidUpdate(prevProps: GridMainProps, prevState: GridMainState) {
    if (prevState.board !== this.state.board) {
      this.triggerChange();
    }
  }

  triggerChange = () => {
    this.props.onChange?.(
      getItems(this.state.board).map((item) => {
        return {
          ...this.itemAttrs[item.id],
          ...item,
          id: this.deRanID(item.id),
        };
      }),
    );
  };

  parseItems = (items: TItem[]) => {
    const itemAttrs: TItemAttrs = {};
    const gridItems = items.map((item) => {
      const { id, w, h, area, x, y, ...rest } = item;
      let _id = this.ranID(id);
      itemAttrs[_id] = rest;
      return {
        id: _id,
        w,
        h,
        area,
        x,
        y,
      };
    });
    return {
      itemAttrs,
      gridItems,
    };
  };

  getPlaceholder = (
    item: Omit<IGridItem, 'x' | 'y'>,
    realx: number,
    realy: number,
  ) => {
    const { board } = this.state;
    const unitWidth = this.unitWidth;
    const unitHeight = this.unitHeight;
    const area = item.area;
    let px = Math.round(realx / unitWidth);
    let py = Math.round(realy / unitHeight);
    let startx = 0;
    let starty = 0;
    let endx = board[0].length;
    let endy = board.length;
    if (area) {
      area.sx && (startx = Math.max(startx, area.sx));
      area.sy && (starty = Math.max(starty, area.sy));
      area.ex && (endx = Math.min(endx, area.ex));
      area.ey && (endy = Math.min(endy, area.ey));
    }
    px = Math.max(px, startx);
    px = Math.min(px, endx - item.w);
    py = Math.max(py, starty);
    py = Math.min(py, endy - item.h);

    return {
      id: '_placeholder_',
      x: px,
      y: py,
      w: item.w,
      h: item.h,
      rx: realx / unitWidth, // 拖入sider时 实际的x比例
      ry: realy / unitHeight,
      rid: item.id,
    };
  };

  handleEnter = () => {
    const rect = this.mainRef.current?.getBoundingClientRect();
    this.mainPageX = rect?.x || 0;
    this.mainPageY = rect?.y || 0;
  };

  handleDragover: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();

    const { pickItem, updatePlaceholder, pickOffset } = this.context;
    if (!pickItem) return false;
    // todo
    const ph = this.getPlaceholder(
      {
        ...pickItem,
        id: this.ranID(pickItem.id),
      },
      e.pageX - this.mainPageX - (pickOffset?.x || 0),
      e.pageY - this.mainPageY - (pickOffset?.y || 0),
    );
    updatePlaceholder(ph);
  };

  handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    itemList?: IGridItem[],
    siderItems?: IGridItem[],
  ) => {
    e.preventDefault();
    const { pickItem, updatePlaceholder, placeholder, updatePickItem } =
      this.context;
    if (!pickItem) return false;
    const { onInsert, onBeforeDrop } = this.props;
    const { board } = this.state;
    const { id, w, h, x, y, area, ...rest } = pickItem!;
    // 处理文件夹
    if (onBeforeDrop) {
      const maybeApp = {
        ...pickItem!,
        x: placeholder!.x,
        y: placeholder!.y,
      };
      const overlaps = getOverlaps(maybeApp, board);
      if (overlaps.length === 1) {
        const maybeFolder = {
          ...overlaps[0],
          ...this.itemAttrs[overlaps[0].id],
        };
        const res = onBeforeDrop(maybeApp, maybeFolder!, this.itemAttrs);
        if (res) {
          this.itemAttrs = res;
          this.forceUpdate();
          this.triggerChange();
          updatePlaceholder(null);
          updatePickItem(null);
          onInsert?.(true, pickItem);
          return false;
        }
      }
    }

    const tmp = {
      x: placeholder!.x,
      y: placeholder!.y,
      w,
      h,
      area,
      id: placeholder!.rid,
    };

    const result = insertItem(tmp, board);

    if (result.success) {
      this.itemAttrs[tmp.id] = rest;

      // 右侧sider做特殊处理
      const itemList = getItems(board);
      const { siderItems } = this.getSiderItems(itemList);
      if (this.doWithSiderItems(itemList, siderItems)) {
        onInsert?.(true, pickItem);
        return false;
      }

      this.setState(
        {
          board: result.board,
          inserting: tmp.id,
        },
        () => {
          updatePlaceholder(null);
          updatePickItem(null);
        },
      );
      onInsert?.(true, pickItem);
    } else {
      updatePlaceholder(null);
      updatePickItem(null);
      onInsert?.(false, pickItem);
    }
  };

  handleDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => {
    // linux 下存在 原生dragleave莫名被触发的bug， 尝试修复它
    if (e.pageX === 0 || e.pageY === 0) return false;
    const { updatePlaceholder } = this.context;
    updatePlaceholder(null);
  };

  /**
   * 手动插入
   */
  manualInsertItem = (item: TItem) => {
    const { w, h, area, x, y, id, ...rest } = item!;
    const { onInsert } = this.props;
    const { board } = this.state;
    const _id = this.ranID(id);
    const result = insertItemIfEmpty({ w, h, area, id: _id }, board);
    if (result.success) {
      this.itemAttrs[_id] = rest;

      this.setState({
        board: result.board,
        inserting: _id,
      });
      onInsert?.(true, item!);
    } else {
      onInsert?.(false, item!);
    }
    return result;
  };

  /**
   * 手动删除
   */
  manualRemoveItem = (item: TItem) => {
    const { board } = this.state;
    const { onRemove, onBeforeRemove } = this.props;
    const realId = this.deRanID(item.id);
    if (!this.itemAttrs[item.id]) {
      onRemove?.(false, { ...item, id: realId });
      return false;
    }
    if (
      onBeforeRemove &&
      !onBeforeRemove({ ...item, id: realId, ...this.itemAttrs[item.id] })
    ) {
      return false;
    }
    const _board = removeItem(item, board);
    this.setState({
      board: _board,
    });
    onRemove?.(true, { ...item, id: realId, ...this.itemAttrs[item.id] });
    delete this.itemAttrs[item.id];
  };

  ranID = (id: string) => {
    return id + '-' + ranNum(5);
  };

  deRanID = (_id: string) => {
    return _id.split('-')[0];
  };

  getEmptyPostion = (item: Omit<IGridItem, 'x' | 'y'>) => {
    const { board } = this.state;
    return getEmptyPosition(item, board);
  };

  // 处理sider居中问题
  getSiderItems = (items: IGridItem[]) => {
    const { _SX_ } = this.context;
    // 得确认sider是在底部还是右边
    if (_SX_) {
      return this.getSiderItemsY(items);
    } else {
      return this.getSiderItemsX(items);
    }
  };

  getSiderItemsY = (items: IGridItem[]) => {
    const { row } = this.props;
    const unitHeight = this.unitHeight;
    const { placeholder, dragging, _SX_ } = this.context;
    // 如果从sider开始拖动，需要减去被拖动的那个
    let siderItems = items.filter((i) => i.x >= _SX_! && i.id !== dragging?.id);
    siderItems = siderItems.map((item) => {
      return {
        ...item,
        y: item.y, // 修正计算
      };
    });
    // 拖动placeholder进来后重新计算位置
    if (siderItems.length < row && placeholder && placeholder.x >= _SX_!) {
      siderItems = siderItems.map((item) => {
        return {
          ...item,
          y: item.y + 1 / (siderItems.length + 1), // 修正计算
        };
      });
      siderItems.push({
        ...placeholder,
        y: placeholder.ry, // 修正计算
      });
    }
    // 计算向下偏移量以达到居中
    const siderOffsetY = (unitHeight * (row - siderItems.length)) / 2;
    const siderYs: any = {};
    siderItems
      .sort((a, b) => a.y - b.y)
      .forEach((item, i) => {
        siderYs[item.id] = i * unitHeight + siderOffsetY;
      });
    return {
      siderItems,
      siderYs,
      siderXs: {},
    };
  };

  getSiderItemsX = (items: IGridItem[]) => {
    const { col } = this.props;
    const unitWidth = this.unitWidth;
    let { placeholder, dragging, _SY_ } = this.context;
    // 如果从sider开始拖动，需要减去被拖动的那个
    let siderItems = items.filter((i) => i.y >= _SY_! && i.id !== dragging?.id);
    siderItems = siderItems.map((item) => {
      return {
        ...item,
        x: item.x,
      };
    });
    // 拖动placeholder进来后重新计算位置
    if (siderItems.length < col && placeholder && placeholder.y >= _SY_!) {
      siderItems = siderItems.map((item) => {
        return {
          ...item,
          x: item.x + 1 / (siderItems.length + 1), // 修正计算
        };
      });
      siderItems.push({
        ...placeholder,
        x: placeholder.rx,
      });
    }
    // 计算向下偏移量以达到居中
    const siderOffsetX = (unitWidth * (col - siderItems.length)) / 2;
    const siderXs: any = {};
    siderItems
      .sort((a, b) => a.x - b.x)
      .forEach((item, i) => {
        siderXs[item.id] = i * unitWidth + siderOffsetX;
      });
    return {
      siderItems,
      siderXs,
      siderYs: {},
    };
  };

  // sider 放置时特殊处理
  doWithSiderItems = (items: IGridItem[], siderItems: IGridItem[]) => {
    const { _SX_ } = this.context;
    if (_SX_) {
      return this.doWithSiderItemsY(items, siderItems);
    } else {
      return this.doWithSiderItemsX(items, siderItems);
    }
  };

  doWithSiderItemsY = (items: IGridItem[], siderItems: IGridItem[]) => {
    const {
      updateDragging,
      updatePlaceholder,
      placeholder,
      _SX_,
      updatePickItem,
    } = this.context;
    if (placeholder!.x < _SX_!) {
      return false;
    }
    if (!siderItems.some((item) => item.id === '_placeholder_')) {
      return false;
    }
    const newSiderItems = siderItems.map((item, i) => {
      return {
        ...item,
        id: item.id === '_placeholder_' ? (item as TPlaceholder)!.rid : item.id,
        x: _SX_!,
        y: i,
      };
    });
    const restItems = items.filter(
      (item) => newSiderItems.findIndex((nitem) => nitem.id === item.id) === -1,
    );
    this.setState(
      {
        board: initBoard(this.props.col, this.props.row, [
          ...restItems,
          ...newSiderItems,
        ]),
      },
      () => {
        updateDragging(null);
        updatePlaceholder(null);
        updatePickItem(null);
      },
    );
    return true;
  };

  doWithSiderItemsX = (items: IGridItem[], siderItems: IGridItem[]) => {
    const {
      updateDragging,
      updatePlaceholder,
      placeholder,
      _SY_,
      updatePickItem,
    } = this.context;
    if (placeholder!.y < _SY_!) {
      return false;
    }
    if (!siderItems.some((item) => item.id === '_placeholder_')) {
      return false;
    }
    const newSiderItems = siderItems.map((item, i) => {
      return {
        ...item,
        id: item.id === '_placeholder_' ? (item as TPlaceholder)!.rid : item.id,
        y: _SY_!,
        x: i,
      };
    });
    const restItems = items.filter(
      (item) => newSiderItems.findIndex((nitem) => nitem.id === item.id) === -1,
    );
    this.setState(
      {
        board: initBoard(this.props.col, this.props.row, [
          ...restItems,
          ...newSiderItems,
        ]),
      },
      () => {
        updateDragging(null);
        updatePlaceholder(null);
        updatePickItem(null);
      },
    );
    return true;
  };

  render() {
    const {
      width: mainWidth,
      height: mainHeight,
      delIcon,
      mainBg,
      onBeforeDrop,
    } = this.props;
    const { board, hideDel, inserting } = this.state;
    const {
      dragging,
      updateDragging,
      placeholder,
      updatePlaceholder,
      pickItem,
    } = this.context;
    const unitWidth = this.unitWidth;
    const unitHeight = this.unitHeight;
    const itemAttrs = this.itemAttrs;
    const itemList = getItems(board);
    const bgStyle = mainBg ? { backgroundImage: `url(${mainBg})` } : {};

    const { siderYs, siderXs, siderItems } = this.getSiderItems(itemList);

    return (
      <div
        className={`grid-main ${pickItem ? 'picking' : ''}`}
        style={{ width: mainWidth, height: mainHeight, ...bgStyle }}
        onDragEnter={this.handleEnter}
        onDragOver={this.handleDragover}
        onDrop={this.handleDrop}
        onDragLeave={this.handleDragLeave}
        ref={this.mainRef}
      >
        {placeholder ? (
          <Motion
            key='ph'
            style={{
              x: spring(
                siderXs['_placeholder_'] ?? placeholder!.x * unitWidth,
                presets.gentle,
              ),
              y: spring(
                siderYs['_placeholder_'] ?? placeholder!.y * unitHeight,
              ),
            }}
          >
            {(style) => (
              <span
                style={{
                  background: 'red',
                  opacity: '0.2',
                  position: 'absolute',
                  width: placeholder.w * unitWidth,
                  height: placeholder.h * unitHeight,
                  zIndex: 98,
                  transform: `translate(${style.x}px, ${style.y}px)`,
                }}
              ></span>
            )}
          </Motion>
        ) : null}
        {itemList.map((item, i: number) => {
          const uw = item.w * unitWidth;
          const ux = siderXs[item.id] ?? item.x * unitWidth;
          const uh = item.h * unitHeight;
          const uy = siderYs[item.id] ?? item.y * unitHeight;
          const isAdding = item.id === inserting;
          return (
            <Motion
              key={item.id}
              onRest={() => {
                inserting && this.setState({ inserting: '' });
              }}
              defaultStyle={{
                x: ux,
                y: uy,
                o: isAdding ? 0 : 1,
              }}
              style={{
                x:
                  item.id === dragging?.id // 拖拽中时 直接赋值拖拽中的x和y
                    ? dragging.dx
                    : spring(ux, presets.noWobble),
                y:
                  item.id === dragging?.id
                    ? dragging.dy
                    : spring(uy, presets.noWobble),
                o: isAdding ? spring(1) : 1,
              }}
            >
              {(style) => {
                return (
                  <Draggable
                    key={item.id}
                    position={{ x: style.x, y: style.y }}
                    onDrag={(e, data) => {
                      const dragging = {
                        ...item,
                        dx: data.x,
                        dy: data.y,
                      };
                      updateDragging(dragging);
                      updatePlaceholder(
                        this.getPlaceholder(item, dragging.dx, dragging.dy),
                      );
                      !hideDel && this.setState({ hideDel: true });
                    }}
                    onStop={(e) => {
                      if (dragging) {
                        // 控制del 显示问题
                        setTimeout(() => {
                          this.setState({
                            hideDel: false,
                          });
                        }, 300);
                        const x = placeholder!.x;
                        const y = placeholder!.y;
                        // 为了处理文件夹插入app而做的特殊处理
                        if (onBeforeDrop) {
                          // onBeforeDrop({ ...item, ...itemAttrs[item.id] })
                          const overlaps = getOverlaps(
                            { ...item, x, y },
                            board,
                          );
                          // 拖入文件夹时出现叠加的item只可能是长度1
                          if (overlaps.length === 1) {
                            const maybeApp = { ...item, ...itemAttrs[item.id] };
                            const maybeFolder = {
                              ...overlaps[0],
                              ...itemAttrs[overlaps[0].id],
                            };
                            const res = onBeforeDrop(
                              maybeApp,
                              maybeFolder!,
                              itemAttrs,
                            );
                            if (res) {
                              this.itemAttrs = res;
                              this.setState(
                                {
                                  board: removeItem(item, board),
                                },
                                () => {
                                  delete this.itemAttrs[item.id];
                                  updateDragging(null);
                                  updatePlaceholder(null);
                                },
                              );

                              return false;
                            }
                          }
                        }
                        // 处理右侧sider
                        if (this.doWithSiderItems(itemList, siderItems)) {
                          return false;
                        }
                        this.setState(
                          {
                            board: moveItem(item, this.state.board, { x, y })
                              .board,
                            inserting: item.id,
                          },
                          () => {
                            updateDragging(null);
                            updatePlaceholder(null);
                          },
                        );
                      }
                    }}
                  >
                    <div
                      className='grid-item'
                      style={{
                        width: uw,
                        height: uh,
                        opacity: style.o,
                        zIndex:
                          dragging?.id === item.id || inserting === item.id
                            ? 100
                            : 99,
                      }}
                    >
                      {itemAttrs[item.id].render?.(
                        item,
                        dragging?.id === item.id ? placeholder : null,
                        dragging?.id === item.id ? dragging : null,
                        this,
                      ) || item.id}
                      {hideDel ? null : (
                        <span
                          className='grid-item-del'
                          onClick={() => {
                            this.manualRemoveItem(item);
                          }}
                        >
                          {delIcon || <i>×</i>}
                        </span>
                      )}
                    </div>
                  </Draggable>
                );
              }}
            </Motion>
          );
        })}
      </div>
    );
  }
}

export default GridMain;
